/**
 * 인증 대기(비공개) 공고 흐름 공용 도우미 — 인스타 광고 유입 → 비회원 작성 → 가입·인증 서류 제출 → 자동 등록.
 *  서버 계약(muyeon-backend docs/ad-funnel-pending-posting-plan.md):
 *   POST /subs/pending · /jobs/pending  body = 일반 등록 body + role + landing → { id, pending }
 *   409 { code: "VERIFICATION_REQUIRED" } → 인증 서류 제출 화면으로
 *   409 { code: "PENDING_LIMIT", message } → 문구 그대로 안내
 *
 * 복귀 경로(funnel redirect): 앱 네이티브 인증 화면은 ?redirect= 를 들고 다니지 않으므로
 *  sessionStorage 에 잠시(1시간) 보관하고, 서류 제출 완료 콜백(AppRouter)에서 꺼내 쓴다.
 */
import { callAddRole, callGetMyRoles } from "api/MuyeonApi";
import { IDENTITY_ROLES } from "constants/memberTypes";
import { openRoleVerification } from "common/nativeBridge";
import { hasNativeBridge } from "util/userAgent";
import { getLandingType } from "util/landingParams";

// 공고 종류별로 인증 대기 등록이 가능한 회원유형(서버와 같은 목록)
export const PENDING_ROLES = {
  SUB: ["TEACHER", "ACADEMY"],
  JOB: ["ACADEMY", "TEAM"],
};

// 인증 대기 공고 작성 후 돌아올 경로
export const PENDING_RESUME_PATH = {
  SUB: "/subs/create?resume=pending",
  JOB: "/jobs/create?resume=pending",
};

export const PENDING_ROLE_LABEL = { TEACHER: "강사", ACADEMY: "학원", TEAM: "공연팀" };

/** 광고 유입 유형으로 고른 기본 회원유형(대타 = 강사, 채용 = 학원). */
export function preferredPendingRole(kind) {
  const lp = getLandingType();
  if (kind === "JOB") return "ACADEMY";
  return lp === "academy" || lp === "academy_job" || lp === "academy_sub" ? "ACADEMY" : "TEACHER";
}

/** 광고 유입 유형에 맞는 가입 후 회원유형(온보딩에서 먼저 보여 줄 유형). 없으면 null. */
export function landingSignupRole() {
  const lp = getLandingType();
  if (lp === "teacher_sub") return "TEACHER";
  if (lp === "academy_job" || lp === "academy_sub") return "ACADEMY";
  return null;
}

/**
 * 인증 대기 등록에 쓸 회원유형을 정한다.
 *  - 서류를 낸(심사 중) 유형이 있으면 그 유형(광고 유형 우선) → submitted: true
 *  - 없으면 광고 유형(기본값) → submitted: false (서류 제출 화면으로 보내야 함)
 *  - 역할 조회가 실패하면 submitted: null — 서버 판정(VERIFICATION_REQUIRED)에 맡긴다.
 */
export async function resolvePendingRole(kind) {
  const allowed = PENDING_ROLES[kind] || [];
  const pref = preferredPendingRole(kind);
  let roles;
  try {
    const r = await callGetMyRoles();
    roles = Array.isArray(r?.data?.roles) ? r.data.roles : null;
  } catch (e) {
    roles = null;
  }
  if (!roles) return { role: pref, submitted: null };
  const ready = roles
    .filter((x) => allowed.includes(x.role) && (x.status === "PENDING" || x.status === "APPROVED"))
    .map((x) => x.role);
  if (ready.includes(pref)) return { role: pref, submitted: true };
  if (ready.length) return { role: ready[0], submitted: true };
  return { role: pref, submitted: false };
}

const FUNNEL_KEY = "muyeon.funnel.redirect";
const FUNNEL_TTL_MS = 60 * 60 * 1000;

const isInternalPath = (p) => typeof p === "string" && p.startsWith("/") && !p.startsWith("//");

export function setFunnelRedirect(path) {
  if (!isInternalPath(path)) return;
  try { sessionStorage.setItem(FUNNEL_KEY, JSON.stringify({ path, at: Date.now() })); } catch (e) { /* noop */ }
}

/** 보관한 복귀 경로를 꺼내고 지운다(없거나 만료면 null). */
export function takeFunnelRedirect() {
  try {
    const raw = sessionStorage.getItem(FUNNEL_KEY);
    sessionStorage.removeItem(FUNNEL_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (!v || !isInternalPath(v.path) || !v.at || Date.now() - v.at > FUNNEL_TTL_MS) return null;
    return v.path;
  } catch (e) {
    return null;
  }
}

/**
 * 가입 후 돌아갈 곳이 인증 대기 공고 자동 등록 화면인가.
 *  ★ 2026-10-11 그렇다면 가입 직후 활동 선택 시트·인증 서류 화면을 건너뛰고 바로 돌아가 공고부터 등록한다
 *   (등록된 공고가 먼저 보여야 이탈이 적다). 인증은 공고를 공개로 바꾸려 할 때 받는다.
 */
export function isPendingResumeRedirect(value) {
  const v = safeRedirect(value);
  return !!v && Object.values(PENDING_RESUME_PATH).includes(v);
}

/**
 * 가입 직후 회원유형 선택 없이 바로 돌아갈 '하던 일' 경로 — 광고 공고 자동 등록 + 레슨 요청 작성(10-24).
 *  레슨 요청 중 가입한 회원에게 유형 선택 창을 띄우면 '일반 회원'을 골라도 레슨 목록으로 가 버려 요청이 끊겼다.
 */
export function isTaskResumeRedirect(value) {
  const v = safeRedirect(value);
  return isPendingResumeRedirect(value) || (!!v && v.startsWith("/quoteRequest"));
}

/** ?redirect= 값 검증 — 내부 경로만 허용(외부 주소로 튕기는 것 방지). */
export function safeRedirect(value) {
  if (!value) return null;
  let v = value;
  try { v = decodeURIComponent(value); } catch (e) { /* 이미 디코딩된 값 */ }
  return isInternalPath(v) ? v : null;
}

/**
 * 인증 서류 제출 화면으로 보낸다. 제출이 끝나면 redirect 로 돌아온다.
 *  앱: 네이티브 서류 첨부 화면(완료 콜백 __onRoleVerify 가 보관한 경로로 이동).
 *  웹: /my/memberType?verify=ROLE&redirect=…
 */
export function goRoleVerification(navigate, role, redirect) {
  // ★ 2026-10-12 강사·무용수는 서류 없이 본인 인증만으로 바로 부여된다 — 부여되면 대기 공고도 서버가 바로 공개한다.
  if (IDENTITY_ROLES.includes(role)) {
    callAddRole(role)
      .then(() => { window.location.assign(redirect || "/mypage"); })
      .catch(() => { /* 본인인증 전(409 IDENTITY_REQUIRED)은 전역 안내(IdentityRequiredHost)가 처리 */ });
    return;
  }
  setFunnelRedirect(redirect);
  if (hasNativeBridge() && openRoleVerification(role)) return;
  const q = `?verify=${encodeURIComponent(role)}${redirect ? `&redirect=${encodeURIComponent(redirect)}` : ""}`;
  navigate(`/my/memberType${q}`);
}

/** 409 응답 코드(VERIFICATION_REQUIRED·PENDING_LIMIT 등). */
export const pendingErrorCode = (e) => (e?.response?.status === 409 ? e?.response?.data?.code || null : null);
